import { Link } from "react-router-dom";
import {
  Car,
  ShieldCheck,
  Check,
  MessageCircle,
  Phone,
  PhoneCall,
  Wrench,
  BadgeCheck,
  Star,
  Clock,
  ArrowRight,
} from "lucide-react";
import { WHATSAPP_URL } from "../lib/whatsapp";
import { PHONE_TEL } from "../lib/contact";
import { useSeo } from "../lib/seo";
import { BodyshopSection } from "../components/BodyshopSection";
import { BookingProofSection } from "../components/BookingProofSection";
import { CallbackModal } from "../components/CallbackModal";

const drivingPoints = [
  "Finding earlier practical driving test dates",
  "Searching UK test centres",
  "Finding suitable availability",
  "Booking support from start to finish",
  "Saving you time and stress",
];

const claimPoints = [
  "Motor claims support",
  "Insurance claim assistance",
  "Help understanding the claims process",
  "Claim forms and documentation",
  "Claim communication",
  "Guidance throughout the process",
];

const heroStats = [
  { icon: BadgeCheck, label: "Insurance Approved" },
  { icon: Wrench, label: "Our Own Bodyshop" },
  { icon: Star, label: "Rated Excellent" },
];

const services = [
  {
    icon: Car,
    title: "Driving Test Support",
    text: "Earlier practical test dates found across UK centres.",
    image: "/service-driving.jpg",
    to: "/driving-tests",
    cta: "Explore",
    testId: "service-card-driving",
  },
  {
    icon: ShieldCheck,
    title: "Motor Claims Assist",
    text: "Clear, end-to-end support through your motor claim.",
    image: "/repair-2.jpg",
    to: "/claims",
    cta: "Explore",
    testId: "service-card-claims",
  },
  {
    icon: Wrench,
    title: "Vehicle Bodywork Repairs",
    text: "Accident repairs & resprays in our own garage.",
    image: "/repair-3.jpg",
    to: "/claims",
    cta: "Explore",
    testId: "service-card-bodywork",
  },
];

const stats = [
  { icon: BadgeCheck, value: "Approved", label: "Insurance work" },
  { icon: Wrench, value: "In-house", label: "Garage & bodyshop" },
  { icon: Clock, value: "Faster", label: "Test date searches" },
  { icon: Star, value: "5-Star", label: "Rated by clients" },
];

const SectionHeading = ({ label, heading, text, testIdPrefix }) => (
  <div>
    <p className="section-label" data-testid={`${testIdPrefix}-label`}>
      {label}
    </p>
    <h2
      data-testid={`${testIdPrefix}-heading`}
      className="font-display mt-4 text-2xl leading-snug text-[#f2ede3] sm:text-[2rem]"
    >
      {heading}
    </h2>
    <p className="mt-5 text-sm leading-relaxed text-[#a09a8c] sm:text-base">{text}</p>
  </div>
);

export default function Home() {
  useSeo(
    "EDT Solutions | Driving Test & Accident Claim Support",
    "EDT Solutions & Services UK helps you find earlier practical driving test dates, supports motor insurance claims, and repairs vehicle bodywork through its own garage. Message us on WhatsApp today."
  );
  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="hero-glow relative overflow-hidden" data-testid="hero-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid items-center gap-10 py-16 sm:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            {/* Left — content */}
            <div>
              <div className="reveal flex items-center gap-4" data-testid="hero-eyebrow">
                <span className="h-px w-10 bg-[#c9a24b]" />
                <p className="section-label">EDT Solutions &amp; Services UK</p>
              </div>
              <h1
                data-testid="hero-heading"
                className="font-display reveal mt-6 max-w-xl text-3xl leading-[1.15] text-[#f2ede3] sm:text-4xl lg:text-5xl"
                style={{ animationDelay: "0.1s" }}
              >
                Driving Tests, Motor Claims &amp; Bodywork{" "}
                <span className="gold-gradient-text">Handled With Care</span>
              </h1>
              <p
                data-testid="hero-subheading"
                className="reveal mt-6 max-w-lg text-sm leading-relaxed text-[#c9c2b4] sm:text-base"
                style={{ animationDelay: "0.2s" }}
              >
                Professional, straightforward support for earlier driving test dates,
                motor insurance claims and vehicle bodywork repairs — handled with care
                from first enquiry to final confirmation.
              </p>
              <div
                className="reveal mt-8 flex flex-wrap items-center gap-3"
                style={{ animationDelay: "0.3s" }}
              >
                <Link
                  to="/driving-tests"
                  data-testid="hero-driving-test-button"
                  className="inline-flex items-center gap-2 bg-[#c9a24b] px-6 py-3 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a]"
                >
                  <Car size={15} />
                  Driving Test Support
                </Link>
                <Link
                  to="/claims"
                  data-testid="hero-insurance-claim-button"
                  className="inline-flex items-center gap-2 bg-[#c9a24b] px-6 py-3 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a]"
                >
                  <ShieldCheck size={15} />
                  Motor Claims Assist
                </Link>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-button"
                  className="inline-flex items-center gap-2 border border-[rgba(201,162,75,0.5)] px-6 py-3 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
                >
                  <MessageCircle size={15} />
                  Message Us
                </a>
              </div>
              <div
                className="reveal mt-9 flex flex-wrap items-center gap-2.5 border-t border-[rgba(201,162,75,0.18)] pt-7"
                style={{ animationDelay: "0.4s" }}
                data-testid="hero-trust-row"
              >
                {heroStats.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 rounded-full border border-[rgba(201,162,75,0.28)] bg-[#111110]/70 px-3.5 py-2"
                  >
                    <Icon size={15} className="text-[#c9a24b]" />
                    <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-[#c9c2b4]">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — image */}
            <div className="reveal relative" style={{ animationDelay: "0.25s" }}>
              <div className="img-card-zoom gold-shadow relative overflow-hidden rounded-2xl border border-[rgba(201,162,75,0.35)]">
                <img
                  src="/hero-car.jpg"
                  alt="Premium vehicle bodywork and repair service by EDT"
                  data-testid="hero-image"
                  className="h-[360px] w-full object-cover sm:h-[460px] lg:h-[520px]"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a09] via-[#0a0a09]/20 to-transparent" />
                <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-[rgba(201,162,75,0.12)]" />
                <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 rounded-xl border border-[rgba(201,162,75,0.35)] bg-[#0a0a09]/70 px-4 py-3 backdrop-blur-md">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#c9a24b] text-[#0a0a09]">
                    <Wrench size={16} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#f2ede3]">
                      Accident Repairs &amp; Resprays
                    </p>
                    <p className="text-[0.7rem] text-[#a09a8c]">
                      Carried out in our own garage
                    </p>
                  </div>
                </div>
              </div>
              <span className="pointer-events-none absolute -right-4 -top-4 -z-10 h-24 w-24 rounded-full bg-[#c9a24b]/20 blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Three services */}
      <section className="border-t border-[rgba(201,162,75,0.18)] bg-[#0e0d0c]" data-testid="home-services">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <p className="section-label">How EDT Helps</p>
            <h2 className="font-display mt-4 text-2xl text-[#f2ede3] sm:text-3xl">
              Three Ways We Take the Pressure Off
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {services.map(({ icon: Icon, title, text, image, to, cta, testId }) => (
              <Link
                key={title}
                to={to}
                data-testid={testId}
                className="feature-card group relative flex flex-col overflow-hidden rounded-2xl border border-[rgba(201,162,75,0.25)] bg-[#111110]"
              >
                <div className="img-card-zoom relative h-44 overflow-hidden">
                  <img src={image} alt={title} className="h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111110] via-[#111110]/40 to-transparent" />
                  <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(201,162,75,0.5)] bg-[#0a0a09]/80 text-[#e6c87a] backdrop-blur-sm">
                    <Icon size={20} />
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-lg text-[#f2ede3]">{title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-[#a09a8c]">{text}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#c9a24b] transition-colors duration-200 group-hover:text-[#e6c87a]">
                    {cta}
                    <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-t border-[rgba(201,162,75,0.18)] bg-[#0a0a09]" data-testid="home-stats">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px overflow-hidden border-x border-[rgba(201,162,75,0.12)] bg-[rgba(201,162,75,0.12)] sm:grid-cols-4">
          {stats.map(({ icon: Icon, value, label }, i) => (
            <div
              key={label}
              data-testid={`home-stat-${i + 1}`}
              className="flex flex-col items-center gap-1 bg-[#0a0a09] px-4 py-8 text-center"
            >
              <Icon size={22} className="text-[#c9a24b]" />
              <p className="font-display mt-2 text-lg text-[#f2ede3]">{value}</p>
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-[#a09a8c]">
                {label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Driving Test Booking Support */}
      <section className="bg-[#0e0d0c]" data-testid="driving-section">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading
              label="Service One"
              heading="Driving Test Booking Support"
              text="Tired of waiting months for a practical test? EDT searches UK test centres for earlier availability and supports you through the booking — so you can spend less time refreshing pages and more time preparing to pass."
              testIdPrefix="driving-section"
            />
            <Link
              to="/driving-tests"
              data-testid="driving-section-button"
              className="mt-8 inline-flex items-center gap-2 bg-[#c9a24b] px-6 py-3 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a]"
            >
              <Car size={15} />
              Driving Test Support
            </Link>
          </div>
          <ul className="rounded-2xl border border-[rgba(201,162,75,0.22)] bg-[#111110] p-2">
            {drivingPoints.map((point, i) => (
              <li
                key={point}
                data-testid={`driving-point-${i + 1}`}
                className="flex items-center gap-4 rounded-xl px-4 py-3.5 transition-colors duration-200 hover:bg-[rgba(201,162,75,0.07)]"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[rgba(201,162,75,0.4)] text-[#c9a24b]">
                  <Check size={15} />
                </span>
                <span className="text-sm text-[#f2ede3]">{point}</span>
              </li>
            ))}
          </ul>
        </div>
        </div>
      </section>

      {/* Insurance Claim Support */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading
              label="Service Two"
              heading="Insurance Claim Support"
              text="Dealing with a motor claim can be confusing and time-consuming. EDT helps you understand the process, prepare your documentation and stay informed at every stage."
              testIdPrefix="claims-section"
            />
            <Link
              to="/claims"
              data-testid="claims-section-button"
              className="mt-8 inline-flex items-center gap-2 border border-[rgba(201,162,75,0.5)] px-6 py-3 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
            >
              <ShieldCheck size={15} />
              Claim Support
            </Link>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {claimPoints.map((point, i) => (
              <li
                key={point}
                data-testid={`claim-point-${i + 1}`}
                className="flex items-center gap-3 rounded-xl border border-[rgba(201,162,75,0.2)] bg-[#0e0d0c] px-4 py-3.5 transition-colors duration-200 hover:border-[rgba(201,162,75,0.5)]"
              >
                <ShieldCheck size={16} className="shrink-0 text-[#c9a24b]" />
                <span className="text-sm text-[#f2ede3]">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Bodyshop / Repair */}
      <BodyshopSection testId="home-bodyshop-section" />

      {/* Driving Test Booking Proof */}
      <BookingProofSection testId="home-booking-proof" />

      {/* Final CTA */}
      <section className="hero-glow relative border-t border-[rgba(201,162,75,0.22)] bg-[#111110]" data-testid="home-final-cta">
        <div className="mx-auto max-w-6xl px-5 py-14 text-center sm:px-8 sm:py-20">
          <div className="mx-auto h-px w-12 bg-[#c9a24b]" />
          <h2
            data-testid="home-final-cta-heading"
            className="font-display mx-auto mt-5 max-w-2xl text-2xl text-[#f2ede3] sm:text-3xl"
          >
            Ready When You Are
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#a09a8c]">
            Whichever way suits you best, EDT is ready to help.
          </p>
          <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-2">
            <Link
              to="/claims#claim-form"
              data-testid="final-cta-claim"
              className="flex items-center justify-center gap-2 border border-[rgba(201,162,75,0.5)] px-6 py-4 text-xs font-bold uppercase tracking-[0.16em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
            >
              <ShieldCheck size={15} />
              Make a Claim
            </Link>
            <Link
              to="/driving-tests#test-form"
              data-testid="final-cta-test"
              className="flex items-center justify-center gap-2 border border-[rgba(201,162,75,0.5)] px-6 py-4 text-xs font-bold uppercase tracking-[0.16em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
            >
              <Car size={15} />
              Book a Driving Test
            </Link>
            <CallbackModal
              trigger={
                <button
                  type="button"
                  data-testid="final-cta-callback"
                  className="flex w-full items-center justify-center gap-2 border border-[rgba(201,162,75,0.5)] px-6 py-4 text-xs font-bold uppercase tracking-[0.16em] text-[#e6c87a] transition-colors duration-200 hover:bg-[#c9a24b] hover:text-[#0a0a09]"
                >
                  <PhoneCall size={15} />
                  Request a Callback
                </button>
              }
            />
            <a
              href={PHONE_TEL}
              data-testid="final-cta-call"
              className="flex items-center justify-center gap-2 bg-[#c9a24b] px-6 py-4 text-xs font-bold uppercase tracking-[0.16em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a]"
            >
              <Phone size={15} />
              Call Us Now
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-whatsapp"
              className="flex items-center justify-center gap-2 bg-[#c9a24b] px-6 py-4 text-xs font-bold uppercase tracking-[0.16em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a] sm:col-span-2"
            >
              <MessageCircle size={15} />
              Message Us Now
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
